"use client";

import { Code, Plus } from "@layer/icons";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@layer/ui/components";
import { cn } from "@layer/utils/cn";
import { useAtom, useAtomValue } from "jotai";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useState } from "react";
import type { ClientDTO } from "@/api/types/clients.types";
import { CreateNewClientDrawerTrigger } from "@/features/client-profile/drawers/add-client-drawer-trigger";
import { clientsAtom } from "@/stores/atoms/clients";
import { selectedClientIdAtom } from "@/stores/atoms/selected-client";

export const ClientSwitcher = () => {
	const t = useTranslations("Common");
	const { isMobile } = useSidebar();

	const clients = useAtomValue(clientsAtom);
	const [selectedClientId, setSelectedClientId] =
		useAtom(selectedClientIdAtom);
	const [addClientOpen, setAddClientOpen] = useState(false);

	const activeClient =
		clients.find((c) => c.clientId === selectedClientId) ?? clients[0];

	if (!activeClient) return null;

	const handleSelectClient = (client: ClientDTO) => {
		setSelectedClientId(client.clientId);
	};

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className={cn(
								"data-[state=open]:bg-sidebar-neutral data-[state=open]:text-sidebar-neutral-foreground",
								"flex items-center leading-none!",
								"group-data-[collapsed=true]/sidebar:justify-center",
								"group-data-[collapsed=true]/sidebar:w-full",
							)}
						>
							<div className="size-8 shrink-0 mr-1 border flex items-center justify-center rounded-lg overflow-hidden group-data-[collapsed=true]/sidebar:mr-0">
								<Image
									src={activeClient.logo}
									alt={activeClient.name}
									width={40}
									height={40}
									className="h-full w-full object-contain"
								/>
							</div>

							<div className="flex flex-col text-left text-sm mt-1 group-data-[collapsed=true]/sidebar:hidden">
								<span className="truncate text-sm text-muted-foreground">
									{t("labels.client")}
								</span>
								<span className="truncate text-base font-medium">
									{activeClient.name}
								</span>
							</div>

							<Code className="rotate-90 ml-auto h-3.5! w-3.5! group-data-[collapsed=true]/sidebar:hidden" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
						side={isMobile ? "bottom" : "right"}
						align="start"
						sideOffset={4}
					>
						<DropdownMenuLabel className="text-muted-foreground text-sm">
							{t("labels.clients")}
						</DropdownMenuLabel>

						{clients.map((client, index) => (
							<DropdownMenuItem
								key={client.clientId}
								onClick={() => handleSelectClient(client)}
								className="gap-2 p-2"
							>
								<div className="flex size-6 items-center justify-center rounded-sm overflow-hidden border bg-background">
									<Image
										src={client.logo}
										alt={client.name}
										width={24}
										height={24}
										className="h-full w-full object-contain"
									/>
								</div>
								<span className="truncate">{client.name}</span>
								<DropdownMenuShortcut>
									⌘{index + 1}
								</DropdownMenuShortcut>
							</DropdownMenuItem>
						))}

						<DropdownMenuSeparator />

						<DropdownMenuItem
							className="gap-2 p-2"
							onSelect={() => setAddClientOpen(true)}
						>
							<div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
								<Plus className="size-4" />
							</div>
							<div className="text-muted-foreground">
								{t("actions.addClient")}
							</div>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
			<CreateNewClientDrawerTrigger
				open={addClientOpen}
				onOpenChange={setAddClientOpen}
			/>
		</SidebarMenu>
	);
};
